import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Alert, Button, Drawer, Input, Space, Spin, Tabs, Typography } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import {
  CheckOutlined,
  FormatPainterOutlined,
  SaveOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  draftScreenConfig,
  extractScreenDiagnostics,
  getScreenVocabulary,
  validateScreenConfig,
  type ScreenConfigView,
  type ScreenDiagnostic,
  type ScreenVocabulary,
} from '@/api/screens';
import { normalizeApiError } from '@/api/errors';
import { BuilderPanel } from './BuilderPanel';
import { DiagnosticsPanel } from './DiagnosticsPanel';
import { PublishModal } from './PublishModal';
import { RoomPreview } from './RoomPreview';
import { lineColOf, locatePointer } from './jsonPointer';
import { newScreen, type JourneyDef } from './model';

const { Text } = Typography;

export interface EditorSeed {
  /** Locked when editing an existing config (a save is always a NEW version of it). */
  configId?: string;
  flowId?: string;
  /** JSON text of an existing version; absent = a fresh three-step starter. */
  definition?: string;
}

function starterJourney(): JourneyDef {
  const screens = [newScreen('UPLOAD', [])];
  screens.push(newScreen('FORM', screens));
  screens.push(newScreen('ACKNOWLEDGE', screens));
  return { locales: ['en'], screens };
}

function parseSeed(seed?: string): JourneyDef {
  if (!seed) return starterJourney();
  try {
    const parsed = JSON.parse(seed);
    return typeof parsed === 'object' && parsed !== null ? (parsed as JourneyDef) : starterJourney();
  } catch {
    return starterJourney();
  }
}

/**
 * The studio's authoring surface, BUILDER-FIRST (owner feedback 2026-08-09: authoring must be
 * visual components — add / edit / delete / reorder — never JSON-as-the-front-door). The
 * canonical state is the typed journey OBJECT; the Builder and the live preview edit/render it
 * directly, and "Advanced (JSON)" is a synced power-user view over the SAME artifact — the
 * FTM dual-representation rule: nothing exists in one view that the other can't express.
 * Validate/Save run the server compile gate; its diagnostics render whole, and in the JSON
 * view each one can be located in the buffer.
 */
export function EditorDrawer({
  seed,
  open,
  onClose,
  onSaved,
}: {
  seed: EditorSeed;
  open: boolean;
  onClose: () => void;
  onSaved: (view: ScreenConfigView) => void;
}) {
  const { message } = App.useApp();
  const areaRef = useRef<TextAreaRef>(null);
  const [configId, setConfigId] = useState(seed.configId ?? '');
  const [flowId, setFlowId] = useState(seed.flowId ?? '');
  const [journey, setJourney] = useState<JourneyDef>(() => parseSeed(seed.definition));
  // The JSON view keeps its own text buffer so half-typed JSON never destroys builder state;
  // a VALID parse flows back into the canonical object immediately.
  const [jsonText, setJsonText] = useState(() => JSON.stringify(parseSeed(seed.definition), null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<ScreenDiagnostic[] | null>(null);
  const [busy, setBusy] = useState<'validate' | 'save' | 'save-publish' | null>(null);
  const [tab, setTab] = useState('builder');
  // "Save & publish" keeps the drawer open under the publish modal; the latest stored draft is
  // remembered so closing the drawer (publish declined) still lands the caller on real state.
  const [savedView, setSavedView] = useState<ScreenConfigView | null>(null);
  const [publishTarget, setPublishTarget] = useState<{ configId: string; version: number } | null>(
    null,
  );

  const [vocabulary, setVocabulary] = useState<ScreenVocabulary | null>(null);
  const [vocabularyError, setVocabularyError] = useState<string | null>(null);
  useEffect(() => {
    getScreenVocabulary()
      .then(setVocabulary)
      .catch((e) => setVocabularyError(normalizeApiError(e).message));
  }, []);

  // The gate, as you type: what is legal is the server's answer, on the control concerned. Stale
  // answers are dropped, and a failed call leaves the last verdict up rather than clearing it.
  useEffect(() => {
    let current = true;
    const id = setTimeout(() => {
      validateScreenConfig(configId.trim() || 'draft', journey)
        .then((result) => {
          if (current) setDiagnostics(result.diagnostics);
        })
        .catch(() => {});
    }, 500);
    return () => {
      current = false;
      clearTimeout(id);
    };
  }, [configId, journey]);

  const previewSource = useMemo(() => ({ definition: journey }), [journey]);

  const setJourneyEverywhere = useCallback((next: JourneyDef) => {
    setJourney(next);
    setJsonText(JSON.stringify(next, null, 2));
    setJsonError(null);
  }, []);

  const onJsonChange = useCallback((text: string) => {
    setJsonText(text);
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed === 'object' && parsed !== null) {
        setJourney(parsed as JourneyDef);
        setJsonError(null);
      } else {
        setJsonError('the definition must be a JSON object');
      }
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const locate = useCallback((path: string) => {
    setTab('json');
    requestAnimationFrame(() => {
      const area = areaRef.current?.resizableTextArea?.textArea;
      if (!area) return;
      const span = locatePointer(area.value, path);
      if (!span) return;
      area.focus();
      area.setSelectionRange(span.start, span.end);
      const line = area.value.slice(0, span.start).split('\n').length;
      area.scrollTop = Math.max(0, (line - 4) * 18);
    });
  }, []);

  const located = useCallback(
    (path: string): string | null => {
      if (jsonError) return null;
      const span = locatePointer(jsonText, path);
      if (!span) return null;
      const { line, col } = lineColOf(jsonText, span.start);
      return `line ${line}:${col}`;
    },
    [jsonText, jsonError],
  );

  const guard = useCallback((): boolean => {
    if (!configId.trim()) {
      message.warning('Give the config an id first (top left).');
      return false;
    }
    if (tab === 'json' && jsonError) {
      message.warning('The JSON view has a syntax error — fix it (or switch back to the Builder).');
      return false;
    }
    return true;
  }, [configId, tab, jsonError, message]);

  const validate = useCallback(async () => {
    if (!guard()) return;
    setBusy('validate');
    try {
      const result = await validateScreenConfig(configId.trim(), journey);
      setDiagnostics(result.diagnostics);
      if (result.ok && result.diagnostics.length === 0) {
        message.success('The gate passes this journey.');
      }
    } catch (e) {
      message.error(normalizeApiError(e).message);
    } finally {
      setBusy(null);
    }
  }, [guard, configId, journey, message]);

  const save = useCallback(
    async (thenPublish: boolean) => {
      if (!guard()) return;
      setBusy(thenPublish ? 'save-publish' : 'save');
      try {
        const view = await draftScreenConfig(configId.trim(), journey, flowId.trim() || undefined);
        message.success(`Draft v${view.version} of '${view.configId}' stored.`);
        setSavedView(view);
        if (thenPublish) {
          // Publishing rides the SAME endpoint + step-up semantics as the studio table — the
          // builder just spares the author the round-trip through the list.
          setPublishTarget({ configId: view.configId, version: view.version });
        } else {
          onSaved(view);
        }
      } catch (e) {
        const gate = extractScreenDiagnostics(e);
        if (gate) {
          setDiagnostics(gate);
          message.error('The compile gate refused this journey — nothing was stored.');
        } else {
          message.error(normalizeApiError(e).message);
        }
      } finally {
        setBusy(null);
      }
    },
    [guard, configId, flowId, journey, message, onSaved],
  );

  return (
    <Drawer
      title={seed.configId ? `New draft of '${seed.configId}'` : 'New screen config'}
      width={1040}
      open={open}
      onClose={() => (savedView ? onSaved(savedView) : onClose())}
      destroyOnClose
      extra={
        <Space>
          {tab === 'json' && (
            <Button
              icon={<FormatPainterOutlined />}
              disabled={!!jsonError}
              onClick={() => setJsonText(JSON.stringify(journey, null, 2))}
            >
              Format
            </Button>
          )}
          <Button icon={<CheckOutlined />} onClick={() => void validate()} loading={busy === 'validate'}>
            Validate
          </Button>
          <Button
            icon={<SaveOutlined />}
            onClick={() => void save(false)}
            loading={busy === 'save'}
          >
            Save draft
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => void save(true)}
            loading={busy === 'save-publish'}
          >
            Save &amp; publish…
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space wrap>
          <div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Config id</div>
            <Input
              value={configId}
              onChange={(e) => setConfigId(e.target.value)}
              placeholder="e.g. kyc-refresh"
              disabled={!!seed.configId}
              className="ol-mono" style={{ width: 260 }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              Flow id <Text type="secondary">(optional — binds the config to a flow)</Text>
            </div>
            <Input
              value={flowId}
              onChange={(e) => setFlowId(e.target.value)}
              placeholder="e.g. onboarding-standard"
              className="ol-mono" style={{ width: 300 }}
            />
          </div>
        </Space>

        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'builder',
              label: 'Builder',
              children: vocabulary ? (
                <BuilderPanel
                  value={journey}
                  onChange={setJourneyEverywhere}
                  vocabulary={vocabulary}
                  diagnostics={diagnostics ?? []}
                />
              ) : vocabularyError ? (
                <Alert
                  type="error"
                  showIcon
                  message="The studio could not read its vocabulary from the server"
                  description={vocabularyError}
                />
              ) : (
                <Spin />
              ),
            },
            {
              key: 'preview',
              label: 'Preview in the room',
              children: (
                <RoomPreview
                  configId={configId}
                  source={previewSource}
                  locales={Array.isArray(journey.locales) ? journey.locales : ['en']}
                />
              ),
            },
            {
              key: 'json',
              label: 'Advanced (JSON)',
              children: (
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    The same journey as its canonical JSON — edits here flow straight back into
                    the Builder the moment they parse. You never need this view; it exists for
                    power users and support.
                  </Text>
                  {jsonError && (
                    <Alert
                      type="error"
                      showIcon
                      message="Not valid JSON — the Builder keeps the last good state"
                      description={<Text className="ol-mono" style={{ fontSize: 12 }}>{jsonError}</Text>}
                    />
                  )}
                  <Input.TextArea
                    ref={areaRef}
                    value={jsonText}
                    onChange={(e) => onJsonChange(e.target.value)}
                    autoSize={{ minRows: 18, maxRows: 28 }}
                    spellCheck={false}
                    className="ol-mono" style={{ fontSize: 12.5, lineHeight: '18px' }}
                  />
                </Space>
              ),
            },
          ]}
        />

        {diagnostics !== null && diagnostics.length > 0 && (
          <DiagnosticsPanel diagnostics={diagnostics} onLocate={locate} located={located} />
        )}
      </Space>

      {/* Publish without leaving the builder — the same modal, endpoint and step-up lane as the
          studio table. Declining keeps the drawer (and the stored draft) exactly as they are. */}
      <PublishModal
        target={publishTarget}
        onClose={() => setPublishTarget(null)}
        onPublished={(view) => {
          setPublishTarget(null);
          onSaved(view);
        }}
      />
    </Drawer>
  );
}
